/**
 * League types and Firestore query helpers.
 *
 * - leagues/{id} — League doc (name, adminUID, authCode, memberCount, competingCount)
 * - leagues/{id}/members/{uid} — leagueMember (rank, score, hasList)
 */
import {
  collection,
  deleteDoc,
  doc,
  type Firestore,
  orderBy,
  type Query,
  query,
  type QueryConstraint,
  type Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

export interface leagueCreationDetails {
  name: string;
}

export default interface League extends leagueCreationDetails {
  adminUID: string;
  authCode: string;
  /** All joined members, including spectators without a list. */
  memberCount?: number;
  /** Members with hasList === true; drives leaderboard UI and pagination. */
  competingCount?: number;
  ranksLastUpdated: Timestamp;
}

export interface joinLeagueResponse {
  uid: string;
  name: string;
  alreadyMember: boolean;
}

/** @deprecated Unused legacy constant; leagues have no entry-fee field in the Firebase app. */
export const defaultEntryFee = 5;

export async function updateLeague(db: Firestore, leagueID: string, data: Partial<League>): Promise<void> {
  return await updateDoc(doc(db, 'leagues', leagueID), data);
}

export async function deleteLeague(db: Firestore, leagueID: string): Promise<void> {
  return await deleteDoc(doc(db, 'leagues', leagueID));
}

export function queryLeagueMembers(db: Firestore, leagueID: string, ...queries: QueryConstraint[]): Query {
  return query(
    collection(db, 'leagues', leagueID, 'members'),
    orderBy('hasList', 'desc'),
    orderBy('currentRank', 'asc'),
    orderBy('joinedLeague', 'asc'),
    ...queries,
  );
}

/** Paginated leaderboard: list-holding members only, in rank order. */
export function queryLeagueLeaderboardMembers(
  db: Firestore,
  leagueID: string,
  ...queries: QueryConstraint[]
): Query {
  return query(
    collection(db, 'leagues', leagueID, 'members'),
    where('hasList', '==', true),
    orderBy('currentRank', 'asc'),
    orderBy('joinedLeague', 'asc'),
    ...queries,
  );
}

export function queryLeagues(db: Firestore, ...queries: QueryConstraint[]): Query {
  return query(collection(db, 'leagues'), ...queries);
}
